import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSidenav } from '@angular/material/sidenav';
import { AppService } from '@App';
import { IConfigService } from 'app/config/iconfigservice';
import { BaseService } from './base.service';
import { teamRequest, TeamPerformanceData, TeamDashboardDetailsDto } from '@Models';
import { RoutesEnum, RoutingService } from './routing.service';
import editTeamRequest from '@Models/global/edit-team-request';
import { GetManageTeamsResponse } from '@App/manage-users/models/get-manage-teams-response.interface';

@Injectable({
    providedIn: 'root'
})
export class TeamsService extends BaseService {

    publicApiBaseUrl: string;
    private sidenav: MatSidenav;

    constructor(
        public appService: AppService, 
        public configService: IConfigService,
        public httpClient: HttpClient,
        public routingService: RoutingService
    ) {
        super(appService, configService, httpClient);
        this.publicApiBaseUrl = this.configService.getConfiguration().publicApiUrl;
    }

    getAllTeams() {
        return super.getData<any>(`${this.publicApiBaseUrl}team/all`);
    }

    getTeam(id, hideWaiting = false) {
        return super.getData<any>(`${this.publicApiBaseUrl}team/${id}`, null, hideWaiting);
    }

    getTeamDetails(clientId, id, hideWaiting = false) {
        return super.getData<any>(`${this.publicApiBaseUrl}global/teams/${clientId}/${id}`, null, hideWaiting);
    }

    getTeamDashboardData(clientId, id, hideWaiting = false) {
        return super.getData<TeamDashboardDetailsDto>(`${this.publicApiBaseUrl}global/teams/${clientId}/${id}/dashboard`, null, hideWaiting);
    }

    getTeamMemberSchedules(id, hideWaiting = false) {
        return super.getData<any>(`${this.publicApiBaseUrl}global/teams/${id}/schedules`, null, hideWaiting);
    }

    getAdminUsersForClient(clientId, hideWaiting = false) {
        return super.getData<any>(`${this.publicApiBaseUrl}global/teams/${clientId}/eligible-admins`, null, hideWaiting)
    }

    getUsersForClient(clientId, hideWaiting = false) {
        return super.getData<any>(`${this.publicApiBaseUrl}global/teams/${clientId}/eligible-users`, null, hideWaiting)
    }

    getUserNotifications(userId, hideWaiting = false) {
        return super.getData<any>(`${this.publicApiBaseUrl}global/teams/notifications/${userId}`, null, hideWaiting);
    }

    getTeamsForUser(userId: string, hideWaiting = false) {
        return super.getData<GetManageTeamsResponse[]>(`${this.publicApiBaseUrl}global/teams/${userId}`, null, hideWaiting);
    }
    
    markAllNotificationsAsRead(userId: string, hideWaiting = false) {
        return super.postData<any>(
            `${this.publicApiBaseUrl}global/teams/notifications/${userId}/markAllRead`, null, null, hideWaiting
        );
    }

    createTeam(payload: teamRequest, hideWaiting = false) {
        return super.postData<any>(`${this.publicApiBaseUrl}global/teams`, payload);
    }

    editTeam(payload: editTeamRequest) {
        return super.patchData(`${this.publicApiBaseUrl}global/teams`, payload);
    }

    routeToTeam(team) {
        const params = {
            teamId: team.id,
        };
        const queryParams = {
            teamName: team.name,
            teamClientId: team.clientId
        };
        this.routingService.navigateToRoute(RoutesEnum.teamOverview, params, queryParams);
    }

    deleteTeam(teamId) {
        return super.deleteData(`${this.publicApiBaseUrl}global/teams/${teamId}`);
    }

    getTeamMembersByAccountAccess(clientId: string, teamId: string, accountIds: string[], hideWaiting = false) {
        return super.postData<any>(`${this.publicApiBaseUrl}global/teams/${clientId}/${teamId}/filter-members-by-account-access`, accountIds || [], null, hideWaiting);
    }

    getCycleTimeKPI(clientId: string, teamId: string, hideWaiting = false) {
        return super.getData<any>(`${this.publicApiBaseUrl}global/teams/${clientId}/${teamId}/get-cycle-time-kpi`, null, hideWaiting);
    }

    getTeamPerformanceData(clientId: string, teamId: string, hideWaiting = false) {
        return super.getData<TeamPerformanceData>(`${this.publicApiBaseUrl}global/teams/${clientId}/${teamId}/get-team-performance-data`, null, hideWaiting);
    }

    public setSidenav(sidenav: MatSidenav) {
        this.sidenav = sidenav;
    }

    public open() {
        return this.sidenav.open();
    }

    public close() {
        return this.sidenav.close();
    }

    public toggle(): void {
        this.sidenav.toggle();
    }
}
